import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListCheck } from "@fortawesome/free-solid-svg-icons";
import { TablePicker } from "./TablePicker";
import { ToolbarButton } from "./ToolbarButton";
import { blockStyles, indentTools, listTools, textTools } from "./toolbarConfig";
import { BlockStyle, FormattingCommand, FormattingTool } from "./types";
import {
    buildTableHtml,
    escapeHtml,
    getSelectionElement,
    getTextBlock,
    normalizeChecklistLists,
} from "./formattingUtils";

import "./NoteFormattingToolbar.css";

interface NoteFormattingToolbarProps {
    editorRef: React.RefObject<HTMLDivElement>;
    onContentChange: () => void;
}

export const NoteFormattingToolbar: React.FC<NoteFormattingToolbarProps> = ({
    editorRef,
    onContentChange,
}) => {
    const [activeCommands, setActiveCommands] = useState<Record<FormattingCommand, boolean>>({
        bold: false,
        italic: false,
        underline: false,
        strikeThrough: false,
        insertUnorderedList: false,
        insertOrderedList: false,
        indent: false,
        outdent: false,
    });
    const [activeBlockStyle, setActiveBlockStyle] = useState<BlockStyle>("div");
    const [isChecklistActive, setIsChecklistActive] = useState(false);
    const [isTablePickerOpen, setIsTablePickerOpen] = useState(false);
    const [hoveredTableSize, setHoveredTableSize] = useState({ rows: 2, columns: 2 });
    const tablePickerRef = useRef<HTMLDivElement>(null);
    const tablePopoverRef = useRef<HTMLDivElement>(null);

    const selectionIsInsideEditor = useCallback(() => {
        const editor = editorRef.current;
        const selection = window.getSelection();
        if (!editor || !selection || selection.rangeCount === 0) return false;

        const range = selection.getRangeAt(0);
        return editor.contains(range.commonAncestorContainer);
    }, [editorRef]);

    const updateActiveCommands = useCallback(() => {
        if (!selectionIsInsideEditor()) {
            setActiveCommands({
                bold: false,
                italic: false,
                underline: false,
                strikeThrough: false,
                insertUnorderedList: false,
                insertOrderedList: false,
                indent: false,
                outdent: false,
            });
            setActiveBlockStyle('div');
            setIsChecklistActive(false);
            return;
        }

        setActiveCommands({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
            strikeThrough: document.queryCommandState('strikeThrough'),
            insertUnorderedList: document.queryCommandState('insertUnorderedList'),
            insertOrderedList: document.queryCommandState('insertOrderedList'),
            indent: false,
            outdent: false,
        });

        const blockValue = document.queryCommandValue('formatBlock').toLowerCase();
        if (blockValue.includes('h2')) {
            setActiveBlockStyle('h2');
        } else if (blockValue.includes('h3')) {
            setActiveBlockStyle('h3');
        } else {
            setActiveBlockStyle('div');
        }

        const element = getSelectionElement();
        setIsChecklistActive(!!element?.closest('ul[data-checklist="true"]'));
    }, [selectionIsInsideEditor]);

    useEffect(() => {
        document.addEventListener('selectionchange', updateActiveCommands);
        return () => document.removeEventListener('selectionchange', updateActiveCommands);
    }, [updateActiveCommands]);

    useEffect(() => {
        if (!isTablePickerOpen) return;

        const closeTablePicker = (event: MouseEvent) => {
            if (
                !tablePickerRef.current?.contains(event.target as Node)
                && !tablePopoverRef.current?.contains(event.target as Node)
            ) {
                setIsTablePickerOpen(false);
            }
        };

        document.addEventListener('mousedown', closeTablePicker);
        return () => document.removeEventListener('mousedown', closeTablePicker);
    }, [isTablePickerOpen]);

    const runIndentCommand = (command: 'indent' | 'outdent') => {
        const editor = editorRef.current;
        if (!editor) return;

        editor.focus();
        const selectedElement = getSelectionElement();

        if (selectedElement?.closest('li')) {
            document.execCommand(command);
            normalizeChecklistLists(editor);
            onContentChange();
            updateActiveCommands();
            return;
        }

        let block = getTextBlock(selectedElement, editor);
        if (!block) {
            document.execCommand('formatBlock', false, 'div');
            block = getTextBlock(getSelectionElement(), editor);
        }

        if (block) {
            const currentIndent = Number(block.getAttribute('data-indent') || 0);
            const nextIndent =
                command === 'indent'
                    ? Math.min(currentIndent + 1, 4)
                    : Math.max(currentIndent - 1, 0);

            if (nextIndent === 0) {
                block.removeAttribute('data-indent');
            } else {
                block.setAttribute('data-indent', String(nextIndent));
            }
        }

        onContentChange();
        updateActiveCommands();
    };

    const runCommand = (command: FormattingTool['command']) => {
        const editor = editorRef.current;
        if (!editor) return;

        if (command === 'indent' || command === 'outdent') {
            runIndentCommand(command);
            return;
        }

        editor.focus();
        document.execCommand(command);
        onContentChange();
        updateActiveCommands();
    };

    const runBlockStyle = (blockStyle: BlockStyle) => {
        const editor = editorRef.current;
        if (!editor) return;

        editor.focus();
        document.execCommand('formatBlock', false, blockStyle);
        onContentChange();
        updateActiveCommands();
    };

    const insertChecklist = () => {
        const editor = editorRef.current;
        const selection = window.getSelection();
        if (!editor || !selection) return;

        editor.focus();
        const selectedText = selection.toString();
        const checklistItems = selectedText
            ? selectedText
                .split('\n')
                .filter((line) => line.trim().length > 0)
                .map((line) => `<li data-checked="false">${escapeHtml(line)}</li>`)
                .join('')
            : '<li data-checked="false"><br></li>';

        document.execCommand('insertHTML', false, `<ul data-checklist="true">${checklistItems}</ul>`);
        onContentChange();
        updateActiveCommands();
    };

    const insertTable = (rows: number, columns: number) => {
        const editor = editorRef.current;
        if (!editor) return;

        editor.focus();
        document.execCommand('insertHTML', false, buildTableHtml(rows, columns));
        setIsTablePickerOpen(false);
        onContentChange();
        updateActiveCommands();
    };

    return (
        <div className="note-formatting-toolbar" aria-label="Note formatting toolbar">
            <select
                className="note-formatting-style-select"
                value={activeBlockStyle}
                aria-label="Text style"
                onChange={(event) => runBlockStyle(event.target.value as BlockStyle)}
            >
                {blockStyles.map((style) => (
                    <option key={style.value} value={style.value}>
                        {style.label}
                    </option>
                ))}
            </select>
            <TablePicker
                isOpen={isTablePickerOpen}
                hoveredSize={hoveredTableSize}
                pickerRef={tablePickerRef}
                popoverRef={tablePopoverRef}
                onToggle={() => setIsTablePickerOpen((isOpen) => !isOpen)}
                onHoverSize={setHoveredTableSize}
                onInsertTable={insertTable}
            />
            {textTools.map((tool) => (
                <ToolbarButton
                    key={tool.ariaLabel}
                    tool={tool}
                    active={activeCommands[tool.command]}
                    onClick={() => runCommand(tool.command)}
                />
            ))}
            {listTools.map((tool) => (
                <ToolbarButton
                    key={tool.ariaLabel}
                    tool={tool}
                    active={activeCommands[tool.command]}
                    onClick={() => runCommand(tool.command)}
                />
            ))}
            <button
                type="button"
                className={`note-formatting-toolbar-button ${isChecklistActive ? 'active' : ''}`}
                aria-label="Checklist"
                aria-pressed={isChecklistActive}
                title="Checklist"
                onMouseDown={(event) => event.preventDefault()}
                onClick={insertChecklist}
            >
                <FontAwesomeIcon icon={faListCheck} />
            </button>
            {indentTools.map((tool) => (
                <ToolbarButton
                    key={tool.ariaLabel}
                    tool={tool}
                    active={activeCommands[tool.command]}
                    onClick={() => runCommand(tool.command)}
                />
            ))}
        </div>
    );
};
